import React from 'react';
import type { ShopSection, Shopping } from '../types';

const sections: ShopSection[] = [
  {
    title: 'No-cook protein',
    icon: 'protein',
    accent: true,
    items: [
      {
        id: 'chicken',
        item: 'Pre-cooked chicken breast',
        detail:
          'Plain only — ingredients must read "chicken, water, salt". No flavoured/seasoned/smoked varieties. Check label every pack.',
        qty: '3 packs (~450 g)',
      },
      {
        id: 'tuna',
        item: 'Tinned tuna',
        detail: 'In spring water, plain — no brine, no mayo, no flavoured varieties',
        qty: '× 3',
      },
    ],
  },
  {
    title: 'Carb staples',
    icon: 'carbs',
    items: [
      { id: 'gf-bread', item: 'GF bread', detail: 'Schär white, Genius, or any plain rice/tapioca loaf', qty: '2 loaves' },
      { id: 'rice', item: 'Jasmine rice', detail: 'Dry weight — or microwave pouches (plain only)', qty: '1 kg' },
      { id: 'gf-pasta', item: 'GF rice pasta', detail: 'Plain — not with added vegetables', qty: '500 g' },
      { id: 'rice-cakes', item: 'Rice cakes', detail: 'Plain, lightly salted', qty: '1 pack' },
      { id: 'rice-bars', item: 'Rice bars', detail: "Kellogg's Rice Krispies Squares or equivalent", qty: '× 7+' },
      {
        id: 'pure',
        item: 'PURE Performance + Race Fuel',
        detail: 'Unflavoured · Cluster Dextrin + sodium in one · pre-race & preload use',
        qty: '2 sachets (or 700 g pouch)',
      },
    ],
  },
  {
    title: 'Fruit',
    icon: 'fruit',
    items: [
      { id: 'bananas', item: 'Firm bananas', detail: 'Must be firm — not ripe/spotty', qty: '× 6' },
      { id: 'strawberries', item: 'Strawberries', qty: '250 g' },
      { id: 'oranges', item: 'Oranges', qty: '× 2' },
      { id: 'kiwi', item: 'Kiwi', qty: '× 1' },
    ],
  },
  {
    title: 'Condiments & flavour',
    icon: 'condiments',
    items: [
      { id: 'jam', item: 'Strawberry jam', qty: '1 jar' },
      { id: 'maple', item: 'Maple syrup', detail: 'Pure — not pancake syrup', qty: '250 mL' },
      { id: 'garlic-oil', item: 'Garlic-infused olive oil', detail: 'Infused, not minced garlic', qty: '100 mL' },
      { id: 'soy', item: 'Soy sauce', qty: 'small bottle' },
      {
        id: 'pasta-sauce',
        item: 'Low-FODMAP pasta sauce',
        detail: 'Fody, or plain passata — check no onion/garlic',
        qty: '1 jar',
      },
      { id: 'salt', item: 'Salt', qty: '1 shaker' },
    ],
  },
  {
    title: 'Drinks',
    icon: 'drinks',
    items: [
      { id: 'milk', item: 'Lactose-containing milk', detail: 'Full cream or semi-skimmed', qty: '1 L' },
      { id: 'oj', item: 'Orange juice', detail: 'No bits', qty: '250 mL' },
      {
        id: 'sports-drink',
        item: 'Sports drink powder',
        detail: 'SiS GO Electrolyte, Precision PF, or Lucozade powder',
        qty: '~5 L worth',
      },
      {
        id: 'gatorade',
        item: 'Gatorade',
        detail: 'Ready-to-drink bottles — backup carb + electrolyte hit',
        qty: '× 4',
      },
      {
        id: 'na-tabs',
        item: 'Sodium electrolyte tabs 500 mg',
        detail: 'LMNT, Precision PH1500, or SiS GO Hydro',
        qty: '1 tube',
      },
      { id: 'coffee', item: 'Coffee', detail: 'Instant or ground — your preference', qty: '—' },
      { id: 'water', item: 'Bottled water', qty: 'as needed' },
    ],
  },
  {
    title: 'Race fuel',
    icon: 'race',
    accent: true,
    items: [
      {
        id: 'gels',
        item: 'Gels',
        detail: 'Maurten Gel 100, SiS GO Isotonic, Precision PF 30, plain GU, or Clif Shot',
        qty: '× 7',
      },
      { id: 'caf-cap', item: 'Caffeine capsule 300 mg', detail: 'ProPlus or Pharma Nord', qty: '× 1' },
    ],
  },
  {
    title: 'Gear',
    icon: 'gear',
    items: [
      {
        id: 'kitchen-scales',
        item: 'Kitchen scales',
        detail: 'For weighing carbs, gels, and portions during fuelling',
        qty: '× 1',
      },
      {
        id: 'weight-scales',
        item: 'Weight scales',
        detail: 'Daily weigh-in during loading week',
        qty: '× 1',
      },
      {
        id: 'travel-adaptor',
        item: 'Travel adaptor',
        detail: 'AU → UK Type G plug — for phone, watch, and chargers',
        qty: '× 1',
      },
    ],
  },
  {
    title: 'Loading supplements · buy first',
    icon: 'supplements',
    accent: true,
    wide: true,
    items: [
      { id: 'tart-cherry', item: 'Tart cherry concentrate', detail: 'Cherry Active or equivalent · already loading', qty: '~800 mL' },
      { id: 'beetroot', item: 'Beetroot shots', detail: 'Beet-It Sport 70 mL · start Mon 20', qty: '× 8' },
      { id: 'nomio', item: 'Nomio shots', detail: 'Start Sun 19 PM · 2/day through Sat 25 PM', qty: '× 15' },
      { id: 'omega3', item: 'Omega-3', detail: 'Routine dose', qty: 'routine' },
      { id: 'mag', item: 'Magnesium glycinate', detail: 'Bedtime', qty: 'routine' },
      { id: 'probiotic', item: 'Probiotic', detail: 'Routine', qty: 'routine' },
    ],
  },
];

export const londonShopping: Shopping = {
  lede: 'Four-day race week + loading · low-FODMAP · lactose OK · tins & no-cook proteins.',
  sections,
  footnote: (
    <>
      <strong>Buy order:</strong> loading supplements first (needed Sun 19 evening) · tinned proteins &amp;
      shelf-stable carbs any time · fruit &amp; bread Thu 23 for freshness · gels &amp; electrolytes by Fri 24 at the
      latest.
    </>
  ),
};
